import classnames from 'classnames';
import React, { useState } from 'react';
import { Button, Nav } from 'react-bootstrap';
import { useQuery } from 'react-query';
import {
  Redirect,
  Route,
  Switch,
  useLocation,
  useRouteMatch,
} from 'react-router';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  DjangoListResponse,
  InvitedUserEntity,
  StrapiApiError,
  ReadOnlyUser,
} from 'v2/models';
import { infiniteStrapiUsersKeys } from 'v2/queries';
import restFactory from 'v2/utils/restFactory';
import styles from '../settingsForm.module.scss';
import ActiveMembersList from './ActiveMembersList';
import InvitedUserList from './InvitedUserList';
import InviteUserModal from './InviteUserModal';

export const INVITE_USER_BTN_TEXT = 'Invite Team Member';
interface MyTeamProps {
  isAdmin: boolean;
  isDataProvider: boolean;
  userId: number;
}

const MyTeam = ({ isAdmin, isDataProvider, userId }: MyTeamProps) => {
  const { url, path } = useRouteMatch();
  const { pathname } = useLocation();
  const ACTIVE_MEMBER_LINK = `${url}/active-members`;
  const INVITED_USERS_LINK = `${url}/invite-users`;
  const defaultActiveKey =
    pathname === INVITED_USERS_LINK ? INVITED_USERS_LINK : ACTIVE_MEMBER_LINK;

  const [showModal, setShowModal] = useState(false);

  const { data: invitedList, isLoading: isLoadingInvitedList } = useQuery(
    'invitedUsers',
    async () => {
      const response = await restFactory.get<
        DjangoListResponse<InvitedUserEntity>
      >('/dj/api/v1/invite_user?remove_accepted_invites=true&sort=email');

      return response;
    },
    {
      onError: (error) => {
        toast.error(`Error loading team. ${error || ''}`);
      },
      refetchOnWindowFocus: false,
      retry: false,
    }
  );

  const { data: teamList, isLoading: isLoadingTeamList } = useQuery(
    infiniteStrapiUsersKeys.all,
    async () => {
      const response = await restFactory.get<DjangoListResponse<ReadOnlyUser>>(
        '/dj/api/v1/strapi_user'
      );

      return response;
    },
    {
      onError: (error: StrapiApiError) => {
        toast.error(`Error loading team. ${error || ''}`);
      },
      refetchOnWindowFocus: false,
      retry: false,
    }
  );

  if (!isDataProvider || !isAdmin) {
    return <Redirect to="/" />;
  }

  return (
    <>
      <div className="mb-5 d-flex align-items-center justify-content-between">
        <h4 className={classnames(styles.title)}>My Team</h4>
        <Button
          className={classnames(
            'btn-secondary-dark',
            'mt-auto align-self-start'
          )}
          variant="secondary"
          onClick={() => setShowModal(true)}
        >
          {INVITE_USER_BTN_TEXT}
        </Button>
      </div>

      <Nav activeKey={defaultActiveKey} className="basic-nav-tab">
        <Nav.Item className="py-3 pl-0">
          <Nav.Link
            className="p-0 pb-3"
            as={Link}
            to={ACTIVE_MEMBER_LINK}
            eventKey={ACTIVE_MEMBER_LINK}
          >
            Members {!isLoadingTeamList ? `(${teamList?.count || 0})` : ''}
          </Nav.Link>
        </Nav.Item>
        <Nav.Item className="py-3 pl-5">
          <Nav.Link
            className="p-0 pb-3"
            as={Link}
            to={INVITED_USERS_LINK}
            eventKey={INVITED_USERS_LINK}
          >
            Invited users{' '}
            {!isLoadingInvitedList ? `(${invitedList?.count || 0})` : ''}
          </Nav.Link>
        </Nav.Item>
      </Nav>
      <div className="px-0 pt-5">
        <Switch>
          <Route path={[`${path}`, `${path}active-members`]} exact>
            <ActiveMembersList list={teamList} currentUserId={userId} />
          </Route>
          <Route path={`${path}invite-users`} exact>
            <InvitedUserList
              list={invitedList}
              isLoading={isLoadingInvitedList}
            />
          </Route>
        </Switch>
      </div>
      <InviteUserModal
        key={String(showModal)}
        show={showModal}
        hide={() => setShowModal(false)}
      />
    </>
  );
};

export default MyTeam;
